/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import './table.scss';
import './renderers/renderers.scss';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { ArrowDown, ArrowUp } from 'react-feather';
import {
  ColumnTypes, RowOverrides, SortOrders, TableStyles,
} from './constants';
import useSelectionStatus from './utils/useSelectionStatus';
import Checkbox, { CheckboxTypes } from '../checkbox';
import useTranslation from '../../../utils/translation-utils';
import Icon, { IconColors, IconSizes } from '../icon';
import ActionItemsCell from './views/action-items-cell/action-items-cell';
import DataCell from './views/data-cell/data-cell';
import Loading from '../loading';
import Paginator from '../paginator';
import DropdownSingleSelect from '../dropdown-single-select';
import EmptyState from '../empty-state';
import ConfigurableColumnsPopover from './utils/configurable-columns-popover';
import ScrollTableButton from './utils/scroll-table-button';

const Table = ({
  selectable, selectedIds, sortBy, sortAsc, columns, data, loading, onAction, actionItems,
  onSelect, onSelectAll, onSortBy, maxActions, showPagination, page, totalCount, pageSize,
  pageSizes, onPageClick, onPageSizeChange, onCellAction, emptyStateProps, columnsConfigurable,
  selectedColumns, style,
}) => {
  const tableRef = useRef(null);
  const tableContRef = useRef(null);
  const [getTranslation] = useTranslation();
  const bulkSelectorState = useSelectionStatus(data, selectedIds);
  const [optionalColumns, setOptionalColumns] = useState(selectedColumns);
  const [open, setOpen] = useState(false);
  const [isRightScrolled, setIsRightScrolled] = useState(false);
  const [isScrollDisabled, setIsScrollDisabled] = useState(false);

  useEffect(() => {
    if (!loading && data.length > 0) {
      tableRef.current.scrollTo(0, 0);
    }
  }, [loading]);

  const visibleColumns = columnsConfigurable ? useMemo(
    () => columns.filter(({ id, optional }) => !optional || optionalColumns.includes(id)),
    [columns, optionalColumns],
  ) : columns;

  const handleBulkSelectClicked = () => {
    onSelectAll(!bulkSelectorState); // true if nothing selected, else false to deselect all
  };

  const handleSelectClicked = (id) => {
    onSelect(id);
  };

  const handleSortByClicked = (id) => {
    onSortBy(id);
  };

  const handleCellAction = (rowId, colId, actionData) => {
    onCellAction(rowId, colId, actionData);
  };

  const handleRowAction = (rowId, actionId, dataItem) => {
    onAction(rowId, actionId, dataItem);
  };

  const checkScrollButtonDisabled = () => {
    const current = tableRef.current || {};
    const { offsetWidth = 0, parentElement } = current;
    const { clientWidth = 0, scrollLeft, scrollWidth } = parentElement || {};
    if ((offsetWidth - clientWidth) === 0) {
      setIsRightScrolled(false);
    } else if (Math.abs(Math.floor(scrollWidth - clientWidth) - scrollLeft) < 1 && scrollLeft > 0) {
      setIsRightScrolled(true);
    } else {
      setIsRightScrolled(false);
    }
    return (offsetWidth - clientWidth) === 0;
  };

  useEffect(() => {
    setIsScrollDisabled(checkScrollButtonDisabled());
  }, [optionalColumns, loading]);

  const scrollTableButtonCallback = () => {
    const { clientWidth, scrollLeft, scrollWidth } = tableContRef.current;
    if (Math.abs(Math.floor(scrollWidth - clientWidth) - scrollLeft) < 1 && scrollLeft > 0) {
      tableContRef.current.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    } else {
      tableContRef.current.scrollTo({
        left: 1000,
        behavior: 'smooth',
      });
    }
  };

  const handleScroll = useCallback(() => {
    const { scrollLeft, scrollWidth, clientWidth } = tableContRef.current;
    if (Math.abs(Math.floor(scrollWidth - clientWidth) - scrollLeft) < 1 && scrollLeft > 0) {
      setIsRightScrolled(true);
    } else {
      setIsRightScrolled(false);
    }
  }, [visibleColumns, optionalColumns]);

  const getBulkSelectorHeaderCell = () => {
    if (selectable) {
      return (
        <th className="tbl-th tbl-header-bulk-select" key="tbl-th-bulk">
          <Checkbox
            type={CheckboxTypes.CHECKBOX}
            checked={!!bulkSelectorState}
            onChange={handleBulkSelectClicked}
            indeterminate={bulkSelectorState === 1}
          />
        </th>
      );
    }
    return null;
  };

  const handleColumnConfigChange = useCallback((id) => () => {
    if (optionalColumns.includes(id)) {
      setOptionalColumns(optionalColumns.filter((colId) => colId !== id));
    } else {
      setOptionalColumns([...optionalColumns, id]);
    }
  }, [visibleColumns]);

  const getSelectorRowCell = (dataItem) => {
    if (selectable) {
      return (
        <td className="tbl-td tbl-row-select" key="tbl-td-select">
          <Checkbox
            type={CheckboxTypes.CHECKBOX}
            checked={selectedIds.includes(dataItem.id)}
            onChange={() => handleSelectClicked(dataItem.id)}
            disabled={dataItem[RowOverrides.DISABLE_SELECTION]}
          />
        </td>
      );
    }
    return null;
  };

  const getActionItemsHeaderCell = () => {
    if (!columnsConfigurable && actionItems.length === 0) {
      return null;
    }
    const classNames = clsx('tbl-th', 'action-items', { configurable: columnsConfigurable });
    return (
      <th
        className={classNames}
        aria-label="actions-header"
        key="tbl-th-actions"
      >
        {columnsConfigurable && (
          <div className="column-config-btn">
            <ConfigurableColumnsPopover
              open={open}
              setOpen={setOpen}
              columns={columns}
              optionalColumns={optionalColumns}
              onSelect={handleColumnConfigChange}
            />
            <ScrollTableButton
              onClick={scrollTableButtonCallback}
              disabled={isScrollDisabled}
              isRightScrolled={isRightScrolled}
            />
          </div>
        )}
      </th>
    );
  };

  const getTableHeaderCell = ({
    id, label, sortable, width, renderCustomHeader,
  }, index) => {
    const styles = { width };
    return (
      <th
        key={String(index)}
        style={styles}
        className={clsx('tbl-th', id, {
          'tbl-th-srtb': sortable,
          'tts-active': id === sortBy,
        })}
      >
        <span className="tbl-th-wrap" onClick={() => sortable && handleSortByClicked(id)}>
          {typeof renderCustomHeader === 'function' ? renderCustomHeader() : label}
          {sortable ? (
            <Icon
              icon={(id === sortBy && !sortAsc)
                ? ArrowDown : ArrowUp}
              size={IconSizes.SMALL}
              color={IconColors.LIGHT_GREY}
              className="tbl-th-sorter"
            />
          ) : null}
        </span>
      </th>
    );
  };

  const getTableHeader = () => (
    <thead className="tbl-thead">
      <tr>
        {getBulkSelectorHeaderCell()}
        {visibleColumns.map((col, index) => getTableHeaderCell(col, index))}
        {getActionItemsHeaderCell()}
      </tr>
    </thead>
  );

  const getDataCell = (col, dataItem, colIndex, rowIndex) => (
    <DataCell
      col={col}
      data={dataItem}
      loading={loading}
      cellKey={`${String(rowIndex)}${String(colIndex)}`}
      onAction={(actionData) => handleCellAction(dataItem.id, col.id, actionData)}
      editable={dataItem[RowOverrides.EDITABLE]}
      inputErrors={dataItem[RowOverrides.FORCED_INPUT_ERRORS]}
    />
  );

  const getTableRow = (dataItem, rowIndex) => (
    <tr key={String(rowIndex)}>
      {getSelectorRowCell(dataItem)}
      {visibleColumns.map((col, colIndex) => getDataCell(col, dataItem, colIndex, rowIndex))}

      <ActionItemsCell
        actionItems={actionItems}
        onAction={(id) => handleRowAction(dataItem.id, id, dataItem)}
        maxActions={maxActions}
        disabledActions={dataItem[RowOverrides.DISABLED_ACTIONS]}
        invisibleActions={dataItem[RowOverrides.INVISIBLE_ACTIONS]}
        disabled={dataItem[RowOverrides.DISABLE_KEBAB_MENU]}
        ctaButtons={dataItem._cta}
      />
    </tr>
  );

  const getEmptyTableState = () => !loading && (
    <tr>
      <td colSpan="100%">{!loading && <EmptyState {...emptyStateProps} />}</td>
    </tr>
  );

  const getTableBody = () => (
    <tbody className={clsx('tbl-tbody', { loading }, { 'hide-pagination': !showPagination })}>
      {data.length > 0 ? data.map((dataItem, index) => getTableRow(dataItem, index))
        : getEmptyTableState()}
    </tbody>
  );

  const getTableFooter = () => {
    if (showPagination && data.length > 0) {
      const footerClass = clsx('tbl-footer', { loading });
      const startIndex = ((page - 1) * pageSize) + 1;
      const endIndex = Math.min(startIndex + pageSize - 1, totalCount);
      return (
        <div className={footerClass}>
          <div className="tbl-footer-brief">
            {getTranslation('TABLE_PAGINATION_BRIEF', {
              range: `${startIndex} - ${endIndex}`,
              total: totalCount,
            })}
          </div>
          <Paginator
            page={page}
            disabled={loading}
            onChange={onPageClick}
            pageCount={Math.ceil(totalCount / pageSize)}
          />
          <DropdownSingleSelect
            disabled={loading}
            onSelect={(id) => onPageSizeChange(+id)}
            items={pageSizes.map((size) => ({
              id: size.toString(),
              label: size.toString(),
              selected: size === pageSize,
            }))}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <div
      ref={tableContRef}
      onScroll={handleScroll}
      className={clsx('tbl-cont', style, { loading })}
    >
      {loading ? <Loading /> : null}
      <table ref={tableRef}>
        {getTableHeader()}
        {getTableBody()}
      </table>
      {getTableFooter()}
    </div>
  );
};

Table.propTypes = {
  columnsConfigurable: PropTypes.bool,
  selectable: PropTypes.bool,
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  sortBy: PropTypes.string,
  sortAsc: PropTypes.bool,
  columns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.oneOf(Object.values(ColumnTypes)),
    label: PropTypes.string,
    sortable: PropTypes.bool,
    width: PropTypes.number,
    accessor: PropTypes.string,
    renderCustomHeader: PropTypes.func,
  })),
  selectedColumns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.oneOf(Object.values(ColumnTypes)),
    label: PropTypes.string,
    sortable: PropTypes.bool,
    width: PropTypes.number,
    accessor: PropTypes.string,
  })),
  data: PropTypes.arrayOf(PropTypes.shape),
  actionItems: PropTypes.arrayOf(PropTypes.shape),
  loading: PropTypes.bool,
  maxActions: PropTypes.number,
  showPagination: PropTypes.bool,
  page: PropTypes.number,
  totalCount: PropTypes.number,
  pageSizes: PropTypes.arrayOf(PropTypes.number),
  pageSize: PropTypes.number,
  onPageClick: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  onSelect: PropTypes.func,
  onSelectAll: PropTypes.func,
  onAction: PropTypes.func,
  onSortBy: PropTypes.func,
  onCellAction: PropTypes.func,
  emptyStateProps: PropTypes.shape({}),
  style: PropTypes.shape({}),
};

Table.defaultProps = {
  columnsConfigurable: false,
  selectedColumns: [],
  selectable: true,
  selectedIds: [],
  sortBy: '',
  sortAsc: false,
  columns: [],
  data: [],
  actionItems: [],
  loading: false,
  maxActions: 2,
  showPagination: true,
  page: 1,
  totalCount: 1,
  pageSizes: [10, 50, 100],
  pageSize: 10,
  onPageClick: () => {},
  onPageSizeChange: () => {},
  onSelect: () => {},
  onSelectAll: () => {},
  onAction: () => {},
  onSortBy: () => {},
  onCellAction: () => {},
  emptyStateProps: {
    text: 'NO_RESULTS_FOUND',
    supportingText: 'TRY_ADJUSTING_SEARCH_FILTER',
  },
  style: {},
};

export {
  Table as default,
  ColumnTypes,
  SortOrders,
  RowOverrides,
  TableStyles,
};
